import { useEffect, useState } from 'react'
import axios from 'axios'
import { Link, useNavigate } from 'react-router-dom'
import {
  getCandidateProfile,
  listMySessions,
  startInterviewFromProfile,
  apiErrorMessage,
  type CandidateProfile,
  type SessionSummary,
} from '@/api/client'
import { AppShell } from '@/components/layout/AppShell'
import { AnimatedBadge } from '@/components/motion/animated-badge'
import { DashSkeleton, StatCard, TrajectoryChart } from '@/components/dashboard/Mosaic'
import { Toaster } from '@/components/ui/toaster'
import { useToast } from '@/hooks/use-toast'
import { usePageMeta } from '@/hooks/use-page-meta'
import { BorderBeam } from 'border-beam'
import { useReducedMotion } from 'motion/react'

const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
const fmtDayYear = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

const CHEVRON = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>
const PLUS = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>

function chipFor(s: SessionSummary) {
  if (s.status === 'in_progress') return <AnimatedBadge status="info" size="sm">in progress</AnimatedBadge>
  if (s.status === 'pending') return <AnimatedBadge size="sm">not finished</AnimatedBadge>
  // The worker marks a session completed the moment finalization begins —
  // before the report lands — so a completed row with no recommendation is
  // still generating, not unscored.
  const rec = s.recommendation
  if (rec === 'Hire' || rec === 'Strong Hire') return <AnimatedBadge status="success" size="sm">{rec}</AnimatedBadge>
  if (rec === 'Hold') return <AnimatedBadge status="warning" size="sm">{rec}</AnimatedBadge>
  if (rec === 'No Hire') return <AnimatedBadge status="danger" size="sm">{rec}</AnimatedBadge>
  return <AnimatedBadge size="sm">report pending</AnimatedBadge>
}

/**
 * Candidate home: the post-login landing point, as a full-viewport mosaic —
 * stats strip with the featured latest result, the score trajectory line,
 * the next action (resume or start), a session index rail, recent rows and
 * the practice-time tile. Feedback stays private to you — recruiters never
 * see this side.
 */
export function CandidateDashboard() {
  usePageMeta('Dashboard — Aura')
  const navigate = useNavigate()
  const { toast } = useToast()
  // The next-up tile's border beam is decoration — reduced motion gets the
  // same tile without the traveling light.
  const reduceMotion = useReducedMotion()
  const [profile, setProfile] = useState<CandidateProfile | null>(null)
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)

  useEffect(() => {
    let cancelled = false
    // Both loads are optional-ish: a missing profile just means no
    // completeness stat and no one-click start.
    getCandidateProfile()
      .then(p => { if (!cancelled) setProfile(p) })
      .catch(() => { /* leave profile null */ })
    listMySessions()
      .then(data => { if (!cancelled) setSessions(data) })
      .catch(err => {
        if (cancelled) return
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          navigate('/', { replace: true })
          return
        }
        setError(apiErrorMessage(err, 'Failed to load your interviews.'))
      })
    return () => { cancelled = true }
  }, [navigate])

  const completed = (sessions ?? []).filter(s => s.status === 'completed')
  // Scored runs oldest → newest for the trajectory; the list API is
  // newest-first, so the arc reverses into chronological order.
  const arc = [...completed]
    .filter(s => s.overall_score !== null)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .map(s => ({ score: s.overall_score as number, created_at: s.created_at }))
  const latest = (sessions ?? []).find(s => s.overall_score !== null) ?? null
  const previousScore = arc.length > 1 ? arc[arc.length - 2].score : null
  const delta = previousScore !== null && latest ? (latest.overall_score as number) - previousScore : null
  const inProgress = (sessions ?? []).find(s => s.status === 'in_progress') ?? null
  const practiceSeconds = completed.reduce((sum, s) => sum + (s.duration_seconds ?? 0), 0)

  // Same 0–100% completeness signal the profile page implies: headline +
  // skills + one experience entry are the minimum a useful interview needs.
  const profileFields = profile
    ? [
        profile.headline.trim(),
        profile.skills.length > 0 ? 'x' : '',
        profile.experience.length > 0 ? 'x' : '',
        profile.summary.trim(),
        profile.location.trim(),
      ]
    : []
  const filled = profileFields.filter(Boolean).length
  const completeness = profile ? Math.round((filled / profileFields.length) * 100) : 0
  const emptyFields = profileFields.length - filled

  const startInterview = async () => {
    setStarting(true)
    try {
      const data = await startInterviewFromProfile()
      navigate(`/interview?session=${data.session_id}`, { replace: true })
    } catch (err) {
      const notFound = axios.isAxiosError(err) && err.response?.status === 404
      toast({
        title: 'Could not start',
        description: notFound
          ? 'No resume on your profile yet — add one on the profile page.'
          : apiErrorMessage(err, 'Could not start an interview from your saved resume.'),
        variant: 'destructive',
      })
      setStarting(false)
    }
  }

  // A running interview is never rejoined: re-entering its room would
  // re-dispatch a fresh agent and burn the session's credits a second time.
  // The report page tracks it instead, and shows the report the moment it
  // lands.
  const trackInterview = () => {
    if (inProgress) navigate(`/my-interviews/${inProgress.session_id}`)
  }
  const openSession = (id: string) => navigate(`/my-interviews/${id}`)

  const loading = sessions === null && !error
  const empty = !loading && !error && (sessions?.length ?? 0) === 0
  const minutes = Math.floor(practiceSeconds / 60)
  const seconds = practiceSeconds % 60

  const startButton = profile?.resume_stored ? (
    <button type="button" className="wbtn wbtn-primary" onClick={startInterview} disabled={starting}>
      {starting ? <><span className="wspinner" /> Preparing…</> : <>New interview {PLUS}</>}
    </button>
  ) : (
    <Link to="/interview" className="wbtn wbtn-primary">New interview {PLUS}</Link>
  )

  return (
    <AppShell active="dashboard">
      <Toaster />
      {error && <div className="wbanner" role="alert">{error}</div>}

      <div className="dash-full">
        <header className="dash-head">
          <div>
            <h1 className="dash-head-title">Dashboard</h1>
            <p className="dash-head-sub">Practice, review, and improve with every run.</p>
          </div>
          <div className="dash-head-actions">
            {startButton}
            <Link to="/my-interviews" className="wbtn wbtn-secondary">My interviews</Link>
          </div>
        </header>

        <section className="dash-stats" aria-label="Your statistics">
          <StatCard
            featured
            label="Latest result"
            value={latest ? latest.overall_score : '—'}
            series={arc.map(p => p.score)}
            hint={
              latest ? (
                <span className="dash-stat-meta">
                  {latest.recommendation} · {fmtDay(latest.created_at)}
                  {delta !== null && delta !== 0 && <em className="dash-delta">{delta > 0 ? `▲ +${delta}` : delta}</em>}
                </span>
              ) : 'No scored runs yet'
            }
          />
          <StatCard
            label="Interviews"
            value={(sessions?.length ?? 0) || '—'}
            hint={(sessions?.length ?? 0) > 0
              ? `${completed.length} scored${inProgress ? ' · 1 in progress' : ''}`
              : 'Nothing yet'}
          />
          <StatCard
            label="Practice time"
            value={practiceSeconds > 0 ? `${minutes}m ${seconds}s` : '—'}
            hint={practiceSeconds > 0 ? 'voiced across completed runs' : 'Nothing on the clock'}
          />
          <StatCard
            label="Profile"
            value={<>{completeness}<span className="dash-stat-unit">%</span></>}
            hint={
              <>
                <span className="dash-stat-meter"><span style={{ width: `${completeness}%` }}></span></span>
                {emptyFields > 0 ? `${emptyFields} field${emptyFields > 1 ? 's' : ''} to go` : 'Complete'}
              </>
            }
          />
        </section>

        {loading ? (
          <DashSkeleton />
        ) : empty ? (
          <div className="dash-empty" role="status">
            <span className="dash-label">Start here</span>
            <strong>Practice makes the report.</strong>
            <p>Run your first voice interview — about 10 minutes — and every tile on this page fills with your real numbers.</p>
            <div className="dash-empty-steps">
              <span>1 · Add your resume</span>
              <span>2 · Optionally paste the job description</span>
              <span>3 · Talk it through, read the report</span>
            </div>
            <Link to="/interview" className="wbtn wbtn-primary">Start your first interview</Link>
          </div>
        ) : (
          <>
            <section className="dash-tile dash-chart" aria-label="Score trajectory">
              <div className="dash-tile-head">
                <h2 className="dash-tile-title">Score trajectory</h2>
                <span className="dash-caption">0–100 across rubric lines</span>
              </div>
              <TrajectoryChart arc={arc} />
            </section>

            {inProgress ? (
              <BorderBeam size="md" colorVariant="ocean" theme="dark" active={!reduceMotion} borderRadius={16} className="dash-next">
                <span className="dash-label">In progress</span>
                <strong className="dash-next-title">Interview running.</strong>
                <span className="dash-next-meta">Started {fmtDayYear(inProgress.created_at)} — its report appears here the moment it ends.</span>
                <button type="button" className="wbtn wbtn-primary" onClick={trackInterview}>Track report</button>
              </BorderBeam>
            ) : (
              <BorderBeam size="md" colorVariant="ocean" theme="dark" active={!reduceMotion} borderRadius={16} className="dash-next">
                <span className="dash-label">Next up</span>
                <strong className="dash-next-title">{latest ? <>Beat {latest.overall_score} next.</> : 'Run your first interview.'}</strong>
                <span className="dash-next-meta">
                  {latest
                    ? `Last run: ${latest.overall_score} · ${latest.recommendation}.`
                    : 'Voice practice, about 10 minutes, structured report.'}
                </span>
                {profile?.resume_stored ? (
                  <button type="button" className="wbtn wbtn-primary" onClick={startInterview} disabled={starting}>
                    {starting ? <><span className="wspinner" /> Preparing…</> : 'Start new'}
                  </button>
                ) : (
                  <Link to="/interview" className="wbtn wbtn-primary">Start new</Link>
                )}
              </BorderBeam>
            )}

            <section className="dash-tile dash-rail-tile" aria-label="Session index">
              <div className="dash-tile-head">
                <h2 className="dash-tile-title">Sessions</h2>
                <Link to="/my-interviews" className="link-action">View all</Link>
              </div>
              <ul className="dash-rail">
                {(sessions ?? []).slice(0, 7).map(s => (
                  <li key={s.session_id}>
                    <button
                      type="button"
                      className="dash-rail-row"
                      disabled={s.status === 'pending'}
                      onClick={() => openSession(s.session_id)}
                    >
                      <span className={`dash-rail-mark${s.overall_score !== null ? ' is-scored' : ''}`}>{s.overall_score ?? '·'}</span>
                      <span className="dash-rail-body">
                        <strong>
                          {s.status === 'in_progress'
                            ? 'In progress'
                            : s.status === 'completed'
                              ? (s.overall_score !== null ? `Scored ${s.overall_score}` : 'Report pending')
                              : 'Not finished'}
                        </strong>
                        <span>{s.recommendation ? `${s.recommendation} · ` : ''}{fmtDayYear(s.created_at)}</span>
                      </span>
                      {s.status !== 'pending' && <span className="dash-rail-go" aria-hidden="true">{CHEVRON}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            <section className="dash-tile dash-tile-rows" aria-label="Recent sessions">
              <div className="dash-tile-head">
                <h2 className="dash-tile-title">Recent sessions</h2>
                <Link to="/my-interviews" className="link-action">All sessions</Link>
              </div>
              <ul className="dash-rows">
                {(sessions ?? []).slice(0, 4).map(s => (
                  <li key={s.session_id}>
                    <button
                      type="button"
                      className="dash-row"
                      disabled={s.status === 'pending'}
                      onClick={() => openSession(s.session_id)}
                    >
                      <span className="dash-row-when">{fmtDayYear(s.created_at)}</span>
                      {chipFor(s)}
                      <strong className="dash-row-score">{s.overall_score ?? '—'}</strong>
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            <div className="dash-dark">
              <span className="dash-label">Practice time</span>
              <strong className="dash-dark-digits">
                {practiceSeconds > 0 ? `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}` : '--:--'}
              </strong>
              <span className="dash-dark-sub">
                {completed.length > 0 ? `voiced across ${completed.length} completed runs` : 'nothing on the clock yet'}
              </span>
            </div>
          </>
        )}
      </div>
    </AppShell>
  )
}
